import axios from '@fs/zion-axios'

// prevents funky redirect loops
// during the authCheck() call
const axiosWithoutRedirect = axios.create()

const BASE_API_URL = '/service/fhcmgmt/scuttlefish/'
const DEFAULT_OPTIONS = {
  headers: {
    Accept: 'application/vnd.familysearch.scuttlefish-v1+json',
    'Content-Type': 'application/vnd.familysearch.scuttlefish-v1+json',
  },
}
const countriesUrl = '/service/user/profile/countries'

export const axiosClient = axios.create()

async function wrapError(req) {
  try {
    return await req
  } catch (err) {
    return err.response
  }
}

export function extractErrorMessage(res) {
  if (res?.data?.errors?.length) {
    return res.data.errors[0]
  }

  return res?.data?.message || ''
}

// admin auth check
// check if the user can call the
// centers endpoint to verify admin role
export function authCheck() {
  return wrapError(axiosWithoutRedirect.get(`${BASE_API_URL}centers`, DEFAULT_OPTIONS))
}

// centers
export const createCenter = (data) => {
  return wrapError(axios.post(`${BASE_API_URL}centers`, data, DEFAULT_OPTIONS))
}

export const editCenter = (data) => {
  return wrapError(axios.put(`${BASE_API_URL}centers/${data.id}`, data, DEFAULT_OPTIONS))
}

export const deleteCenter = (id) => {
  return wrapError(axios.delete(`${BASE_API_URL}centers/${id}`, DEFAULT_OPTIONS))
}

export function listCenters() {
  return wrapError(axios.get(`${BASE_API_URL}centers`, DEFAULT_OPTIONS))
}

// CSV Handler
export function uploadCSV(file) {
  const formData = new FormData()
  formData.append('uploadFile', file)
  return wrapError(
    axios.post(`${BASE_API_URL}csv/upload`, formData, {
      headers: {
        Accept: 'multipart/form-data',
        'Content-Type': 'multipart/form-data',
      },
    })
  )
}

export function downloadCSV() {
  return wrapError(axios.get(`${BASE_API_URL}csv`))
}

// controlGroups/ACLS/roles
export function listControlGroups() {
  return wrapError(axios.get(`${BASE_API_URL}control-groups`, DEFAULT_OPTIONS))
}

export function getControlGroup(id) {
  return wrapError(axios.get(`${BASE_API_URL}control-groups/${id}`, DEFAULT_OPTIONS))
}

export function createControlGroup(name, associateSiteIds) {
  const payload = {
    name,
    associateSiteIds,
  }

  return wrapError(axios.post(`${BASE_API_URL}control-groups`, payload, DEFAULT_OPTIONS))
}

export function updateControlGroup(id, name, associateSiteIds) {
  const payload = {
    id,
    name,
    associateSiteIds,
  }

  return wrapError(axios.put(`${BASE_API_URL}control-groups/${id}`, payload, DEFAULT_OPTIONS))
}

export function deleteControlGroup(id) {
  return wrapError(axios.delete(`${BASE_API_URL}control-groups/${id}`, DEFAULT_OPTIONS))
}

// partners
export function listPartners() {
  return wrapError(axios.get(`${BASE_API_URL}partners`, DEFAULT_OPTIONS))
}

export function createPartner(name, description) {
  const payload = {
    name,
    description,
  }

  return wrapError(axios.post(`${BASE_API_URL}partners`, payload, DEFAULT_OPTIONS))
}

export function updatePartner(partnerId, name, description) {
  const payload = {
    id: partnerId,
    name,
    description,
  }

  return wrapError(axios.put(`${BASE_API_URL}partners/${partnerId}`, payload, DEFAULT_OPTIONS))
}

export function deletePartner(id) {
  return wrapError(axios.delete(`${BASE_API_URL}partners/${id}`, DEFAULT_OPTIONS))
}

// partner sites
export function listPartnerSites(partnerId) {
  return wrapError(axios.get(`${BASE_API_URL}partners/${partnerId}/sites`, DEFAULT_OPTIONS))
}

export function createPartnerSite(partnerId, partnerSite) {
  return wrapError(axios.post(`${BASE_API_URL}partners/${partnerId}/sites`, partnerSite, DEFAULT_OPTIONS))
}

export function updatePartnerSite(partnerId, partnerSite) {
  return wrapError(
    axios.put(`${BASE_API_URL}partners/${partnerId}/sites/${partnerSite.id}`, partnerSite, DEFAULT_OPTIONS)
  )
}

export function deletePartnerSite(partnerId, partnerSiteId) {
  return wrapError(axios.delete(`${BASE_API_URL}partners/${partnerId}/sites/${partnerSiteId}`, DEFAULT_OPTIONS))
}

// get all centers' manual ip lists
export function getManualIpLists() {
  return wrapError(axios.get(`${BASE_API_URL}manual-sources`, DEFAULT_OPTIONS))
}

// set a center's manual ip list
export function updateManualIpList(centerId, manualIpList) {
  const updatedCenterManualIpList = {
    manualSources: manualIpList
      .filter((x) => x)
      .map((ip) => ({
        inetAddress: ip,
      })),
  }

  return wrapError(
    axios.put(`${BASE_API_URL}centers/${centerId}/manual-sources`, updatedCenterManualIpList, DEFAULT_OPTIONS)
  )
}

// list all centers' meraki ip addresses
export function listCenterUnitSources(centerId) {
  return wrapError(axios.get(`${BASE_API_URL}centers/${centerId}/unit-sources`, DEFAULT_OPTIONS))
}

export function getUsersCenters() {
  return wrapError(axios.get(`/service/fs/locator/admin/facility`))
}

export function createNewFacility(facility) {
  return wrapError(axios.post(`/service/fs/locator/admin/facility`, facility))
}

export function deleteFacility(facility) {
  return wrapError(axios.delete(`/service/fs/locator/admin/facility/${facility.facilityId}`, facility))
}

export function searchCenters(searchString, searchType) {
  return wrapError(
    axios.get(`/service/fs/locator/admin/search?searchType=${searchType}&searchVariable=${searchString}`)
  )
}

export function getSelectedCenter(unitNumber) {
  return wrapError(axios.get(`/service/fs/locator/admin/facility/unit-number=${unitNumber}`))
}

export async function getCountries() {
  return axios.get(countriesUrl).then((res) => {
    return res?.data?.places || []
  })
}

export function getCenters(facilityId) {
  return wrapError(axios.get(`/service/fs/locator/admin/facility/${facilityId}`))
}

export function updateCenterData(updates) {
  return wrapError(axios.put(`/service/fs/locator/admin/facility`, updates))
}

export function getCenterAddress(addressId) {
  return wrapError(axios.get(`/service/fs/locator/admin/address/${addressId}`))
}

export function getCenterHours(facilityId) {
  return wrapError(axios.get(`/service/fs/locator/admin/hours/facility-id=${facilityId}`))
}

export function updateCenterHours(updates) {
  return wrapError(axios.post(`/service/fs/locator/admin/hours`, updates))
}
export function getCenterEvents(facilityId) {
  return wrapError(axios.get(`/service/fs/locator/admin/calendar/facility-id=${facilityId}`))
}

export function updateCenterRepresentatives(updates) {
  return wrapError(axios.post(`/service/fs/locator/admin/affiliateContacts`, updates))
}

export function getCenterRepresentatives(facilityId) {
  return wrapError(axios.get(`/service/fs/locator/admin/affiliateContacts/facility-id=${facilityId}`))
}

export function createCenterEvent(updates) {
  return wrapError(axios.post(`/service/fs/locator/admin/calendar`, updates))
}

export function updateCenterEvent(updates) {
  return wrapError(axios.put(`/service/fs/locator/admin/calendar`, updates))
}

export function deleteCenterEvent(calendarId) {
  return wrapError(axios.delete(`/service/fs/locator/admin/calendar/${calendarId}`))
}

export function getEditingPermissions() {
  return wrapError(axios.get(`/service/help/local-info/centers/editor`))
}

export function getCenterDirectorPermissions() {
  return wrapError(axios.get(`/service/help/local-info/centers/leader`))
}

export function getCenterOutageMessage() {
  return wrapError(axios.get(`/service/fs/locator/admin/systemMessage`))
}

export function downloadPrinterCSV() {
  return wrapError(
    axios.get(`/service/fs/locator/admin/printers`, {
      headers: {
        Accept: 'text/csv',
      },
    })
  )
}

export function downloadComputerCSV() {
  return wrapError(
    axios.get(`/service/fs/locator/admin/computers`, {
      headers: {
        Accept: 'text/csv',
      },
    })
  )
}

export function downloadAffiliateDetailsCSV() {
  return wrapError(
    axios.get(`/service/fs/locator/admin/affiliates`, {
      headers: {
        Accept: 'text/vnd.familysearch.fs-locator-admin.details+csv;charset=UTF-8',
      },
    })
  )
}

export function downloadAffiliateOnboardingCSV() {
  return wrapError(
    axios.get(`/service/fs/locator/admin/affiliates`, {
      headers: {
        Accept: 'text/vnd.familysearch.fs-locator-admin.onboarding+csv;charset=UTF-8',
      },
    })
  )
}

export function getUploadList(facilityId) {
  return wrapError(axios.get(`/service/fs/locator/admin/uploads?facilityId=${facilityId}`))
}

export function deleteUpload(uploadId) {
  return wrapError(axios.delete(`/service/fs/locator/admin/uploads/${uploadId}`))
}

export function addUpload(upload) {
  return wrapError(axios.post(`/service/fs/locator/admin/uploads`, upload))
}

export function updateUpload(upload) {
  return wrapError(axios.put(`/service/fs/locator/admin/uploads/${upload.id}`, upload))
}

export function downloadAttachment(uploadId) {
  return axios.get(`/service/fs/locator/admin/uploads/${uploadId}/file`, {
    headers: {
      Accept: 'application/octet-stream',
    },
    responseType: 'blob',
  })
}

export function getUpload(uploadId) {
  return axios
    .get(`/service/fs/locator/admin/uploads/${uploadId}/file`, {
      responseType: 'blob',
    })
    .then((response) => {
      if (response && response.data instanceof Blob) {
        return response.data
      }
      throw new Error('Response is not a Blob')
    })
    .catch((err) => {
      console.error('Error fetching upload:', err)
      throw err
    })
}

export function getPrinters(facilityId) {
  return wrapError(axios.get(`/service/fs/locator/admin/printer/facility-id=${facilityId}`))
}

export function getComputers(facilityId) {
  return wrapError(axios.get(`/service/fs/locator/admin/computer/facility-id=${facilityId}`))
}

export function editPrinter(updates) {
  return axios.put(`/service/fs/locator/admin/printer`, updates)
}

export function addPrinter(updates) {
  return axios.post(`/service/fs/locator/admin/printer`, updates)
}

export function getPrinterModels() {
  return wrapError(axios.get(`/service/fs/locator/admin/printerModel/all`))
}

export function addPrinterModel(updates) {
  return axios.post('/service/fs/locator/admin/printerModel', updates)
}

export function deletePrinterModel(modelId) {
  return axios.delete(`/service/fs/locator/admin/printerModel/${modelId}`)
}

export function deletePrinter(printerId) {
  return axios.delete(`/service/fs/locator/admin/printer/${printerId}`)
}

export function getAreaInformation(facilityId) {
  return axios.get(`/service/fs/locator/admin/fmGroup/facility-id=${facilityId}`)
}

export function getDirectorInformation(facilityId) {
  return axios.get(`/service/fs/locator/admin/director/facility-id=${facilityId}`)
}

export function getFacilityDailyReport(facilityId) {
  return axios.get(`/service/fs/locator/admin/computer-usages/${facilityId}/centersDaily`)
}

export function getComputerUsageData(facilityId) {
  return axios.get(`/service/fs/locator/admin/facilities/${facilityId}/computer-usage`)
}

// Data for center counts page and custom facility reports page
export function getCountriesByArea(areas) {
  if (!areas || areas.length === 0) {
    return axios.get(`/service/fs/locator/admin/facilities/report/countries`)
  }
  return axios.get('/service/fs/locator/admin/facilities/report/countries-by-areas', {
    params: { fhcAreas: areas.join(',') },
  })
}

export function getRegionByCountry(countries) {
  if (!countries || countries.length === 0) {
    return Promise.resolve({ data: [] })
  }
  return wrapError(
    axios.get('/service/fs/locator/admin/facilities/report/regions-by-countries', {
      params: { countryNames: countries.join(',') },
    })
  )
}

export function getCityByRegion(regionStateProvince) {
  if (!regionStateProvince || regionStateProvince.length === 0) {
    return Promise.resolve({ data: [] })
  }
  return axios.get('/service/fs/locator/admin/facilities/report/cities-by-regions', {
    params: { regionStateProvinces: regionStateProvince.join(',') },
  })
}

export function getFhcAreas() {
  return wrapError(axios.get(`/service/fs/locator/admin/facilities/report/fhc-areas`))
}

// center counts page
export function getCenterCounts(filterGroups, facilityType = 'FamilySearch center') {
  return axios.post(`/service/fs/locator/admin/facilities/counts`, {
    filterGroups, // array of { fhcArea, country, regionStateProvince, city }
    facilityType,
  })
}

export function getTempleDistricts(filterCountry) {
  if (filterCountry && filterCountry.length > 0) {
    return wrapError(
      axios.get(`/service/fs/locator/admin/lookups/templeDistricts`, {
        params: { countries: filterCountry.join(',') },
      })
    )
  }

  return wrapError(axios.get(`/service/fs/locator/admin/lookups/templeDistricts`))
}

// custom center reports
export function getFacilityReport(report, format = 'application/json') {
  const orderBy = report.orderByFields.map((item) => `${item.field} ${item.order}`).join(', ')

  const params = {
    fields: report.fields?.join(',') || undefined,
    orderBy,
    countryName: report.country || undefined,
    facilityType: report.fhcType || 'FamilySearch center',
    fhcArea: report.fhcArea || undefined,
    regionStateProvince: report.state || undefined,
    templeDistrict: report.templeDistrict || undefined,
    languageDisplay: report.language || undefined,
    additionalParams: report.additionalParams || undefined,
  }

  return wrapError(
    axios.get(`/service/fs/locator/admin/facilities/report`, {
      params,
      headers: {
        Accept: format,
      },
      responseType: format === 'application/json' ? 'json' : 'blob',
    })
  )
}

export function getReportPresets() {
  return wrapError(axios.get('/service/fs/locator/admin/report-presets'))
}

export function createReportPreset(data) {
  // Transform the form data to match the API expected format
  const presetData = {
    name: data.reportAndPresetName,
    reportType: data.fhcType === 'FamilySearch center' ? 'FAMILYSEARCH_CENTER' : 'FAMILYSEARCH_AFFILIATE',
    fieldsJson: JSON.stringify(data.fields || []),
    filtersJson: JSON.stringify({
      fhcArea: data.fhcArea || undefined,
      countryName: data.country || undefined,
      regionStateProvince: data.state || undefined,
      templeDistrict: data.templeDistrict || undefined,
      facilityType: data.fhcType || undefined,
    }),
    sortOrderJson: JSON.stringify(data.orderByFields || []),
  }

  return wrapError(axios.post('/service/fs/locator/admin/report-presets', presetData))
}

export function updateReportPreset({ presetId, data }) {
  // Transform the form data to match the API expected format
  const presetData = {
    name: data.reportAndPresetName,
    reportType: data.fhcType === 'FamilySearch center' ? 'FAMILYSEARCH_CENTER' : 'FAMILYSEARCH_AFFILIATE',
    fieldsJson: JSON.stringify(data.fields || []),
    filtersJson: JSON.stringify({
      fhcArea: data.fhcArea || undefined,
      countryName: data.country || undefined,
      regionStateProvince: data.state || undefined,
      templeDistrict: data.templeDistrict || undefined,
      facilityType: data.fhcType || undefined,
    }),
    sortOrderJson: JSON.stringify(data.orderByFields || []),
  }

  return wrapError(axios.put(`/service/fs/locator/admin/report-presets/${presetId}`, presetData))
}

export function deleteReportPreset(presetId) {
  return wrapError(axios.delete(`/service/fs/locator/admin/report-presets/${presetId}`))
}
